import React, { useState } from 'react';
import { GiftIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { rewardStatusLabels } from '../../data/labels';
import { Reward } from '../../types/family';
import { rewardVisibleTo } from '../../utils/rewards';
import { RewardStatus } from '../../types/shared';
import { Button } from '../ui/Button';
import { RewardRequestSheet } from './RewardRequestSheet';
const statusCls: Record<RewardStatus, string> = {
  caka: 'bg-review-soft text-review-ink',
  odobreno: 'bg-info-soft text-info-ink',
  koristeno: 'bg-done-soft text-done-ink',
  zavrnjeno: 'bg-excused-soft text-excused-ink'
};
export function RewardsSection({
  memberId
}: {
  memberId: string;
}) {
  const {
    family,
    db,
    balanceOf,
    viewer,
    cancelReward
  } = useAppState();
  const [picked, setPicked] = useState<Reward | null>(null);
  const balance = balanceOf(memberId);
  const own = memberId === viewer.id;
  const requests = db.rewardRequests.filter(r => r.familyId === family.id && r.memberId === memberId);
  const rewards = family.rewards.filter(r => rewardVisibleTo(r, memberId)).sort((a, b) => a.cost - b.cost);
  return <section aria-labelledby="rewards" data-id="element-364" data-mp="22v374ec61b1u">
      <h2 id="rewards" className="font-display text-xl font-semibold" data-id="element-365" data-mp="2gsrsdna5ak7f">
        Nagrade
      </h2>
      <p className="text-xs text-muted" data-id="element-366" data-mp="2bt6snan5i7tn">Izbereš nagrado, starš potrdi in skupaj določita termin.</p>
      {rewards.length === 0 ? <p className="mt-3 rounded-2xl bg-canvas p-4 text-sm text-muted" data-id="element-367" data-mp="1o4dquxjul5r4">Starša še nista izbrala nagrad zate.</p> : <ul className="mt-3 grid gap-3 sm:grid-cols-2" data-id="element-368" data-mp="1k0fsip2cjyz4">
          {rewards.map(r => {
        const pct = Math.min(100, Math.round(balance / r.cost * 100));
        return <li key={r.id} className="flex flex-col rounded-3xl border-[1.5px] border-line bg-surface p-4" data-id="element-369" data-mp="1t0uiw2u6449h">
                <div className="flex items-start gap-3" data-id="element-370" data-mp="15b06fmdrghpr">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-bonus-soft text-bonus-ink" data-id="element-371" data-mp="10di6umlmveuf">
                    <GiftIcon className="h-5 w-5" aria-hidden="true" data-id="element-372" data-mp="30g9cb4neprk8" />
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-373" data-mp="35drn70kziguv">
                    <span className="block font-bold leading-snug" data-id="element-374" data-mp="3a5swqeqrpth7">{r.title}</span>
                    <span className="block text-sm font-extrabold text-bonus-ink" data-id="element-375" data-mp="3f55sjozmaavx">{r.cost} goodies</span>
                  </span>
                </div>
                <div className="mt-auto pt-3" data-id="element-376" data-mp="2hj847ayk3bc2">
                  <div className="h-2 overflow-hidden rounded-full bg-line" aria-hidden="true" data-id="element-377" data-mp="2mgi099ggal8v">
                    <div className="h-full rounded-full bg-bonus" style={{
                width: `${pct}%`
              }} data-id="element-378" data-mp="2rg2zlkzj8vm4" />
                  </div>
                  {own && <Button size="sm" variant={balance >= r.cost ? 'bonus' : 'secondary'} className="mt-3 w-full" disabled={balance < r.cost} onClick={() => setPicked(r)} data-id="element-379" data-mp="2vizotujkvpz1">
                      {balance >= r.cost ? 'Zahtevaj' : `Še ${r.cost - balance} goodies`}
                    </Button>}
                </div>
              </li>;
      })}
        </ul>}
      {requests.length > 0 && <div className="mt-5" data-id="element-380" data-mp="1ra0lyzefhfib">
          <h3 className="text-sm font-extrabold" data-id="element-381" data-mp="1mgehvvmbyjy4">Moje zahteve</h3>
          <ul className="mt-2 divide-y divide-line rounded-3xl border-[1.5px] border-line bg-surface" data-id="element-382" data-mp="1melage3w7vp2">
            {requests.map(r => <li key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-id="element-383" data-mp="1cj0eawli9pn8">
                <span className="min-w-0 flex-1" data-id="element-384" data-mp="17j5o6v9k1438">
                  <span className="block font-bold" data-id="element-385" data-mp="17iccrgznhqer">{r.title}</span>
                  <span className="block text-xs text-muted" data-id="element-386" data-mp="13j3k6fgcjyks">
                    {r.slot} · {r.cost} goodies{r.note ? ` · ${r.note}` : ''}
                  </span>
                </span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${statusCls[r.status]}`} data-id="element-387" data-mp="2xwmm01heu3bw">{rewardStatusLabels[r.status]}</span>
                {own && r.status === 'caka' && <Button size="sm" variant="ghost" onClick={() => cancelReward(r.id)} data-id="element-388" data-mp="1plkumdelp8qg">
                    Prekliči
                  </Button>}
              </li>)}
          </ul>
        </div>}
      <RewardRequestSheet reward={picked} onClose={() => setPicked(null)} data-id="element-389" data-mp="1gkczu5bamq14" />
    </section>;
}